import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Navigate, Routes, Route } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Divider,
  TextField,
  InputAdornment,
  SvgIcon,
  useMediaQuery,
  CssBaseline,
  Paper,
  alpha,
  Fade,
  CircularProgress,
  Theme,
  ThemeProvider,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AirlineStops as SMAIcon,
  ViewList as WatchlistIcon,
  FilterList as ScreenerIcon,
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIconMui, // Renamed SearchIcon import
  PersonAdd as AddIcon,
  Google as GoogleIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Build as ToolsIcon,
  Bookmarks as ProfilesIcon,
  Category as SectorIcon,
  ShowChart as ShowChartIcon,
  Lightbulb as RecommendationsIcon,
  Insights as InsightsIcon,
  ChevronRight as ChevronRightIcon,
  QuestionAnswer as QuestionAnswerIcon,
  PieChart as PieChartIcon,
  BarChart as MarketCapIcon, // Renamed DragHandle import
  Map as StockDiscoveryIcon,
  NewReleases as NewReleasesIcon,
  KeyboardDoubleArrowUp as ArrowUpwardIcon,
  Info as InfoIcon,
  Help as HelpIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  MonetizationOn as MonetizationOnIcon,
  Description,
  AssignmentTurnedIn,
  AutoGraph,
  Tune,
  ScatterPlot,
  BubbleChart,
} from '@mui/icons-material';

import UnregisteredContent from './components/content/UnregisteredContent';
import PremiumContent from './components/content/PremiumContent';
import TickerDetailView from './components/TickerDetailView';
import AboutUsPage from './components/content/AboutUsPage';
import ContactPage from './components/content/ContactPage';
import UserProfilePage from './components/content/UserProfilePage';
import { RegistrationWorkflow, SubscriptionManager } from './components/auth';
import InvestorToolsPage from './components/content/InvestorToolsPage';
import {version} from './MetaVariables';
import { useAuth} from './contexts/AuthContext'; // Assuming User type is exported
import { useProfile } from './contexts/ProfileContext'; // Assuming Profile type is exported
import { useLoadingStatus } from './contexts/LoadingStatusContext';
import LoadingIndicator from './components/common/LoadingIndicator';
import TickerBanner from './components/common/TickerBanner';
import { BrowserRedirectBanner } from './components/common/BrowserRedirectBanner';
import { clearCache } from './utils/memoryCache';
import { investmentAPI } from './api/investmentAPI'; // Assuming MarketReport type is exported
import { XIcon, DRAWER_WIDTH, MARKET_ANALYSIS_SUBPAGES } from './NewApp.constants'; // Import from constants file
import { MarketReportFull } from './types/investment';
import { User } from './types/auth'; // Assuming User type is exported
import ThemeToggle from './components/common/ThemeToggle'; // Assuming ThemeToggle is a component for theme switching
import FAQPage from './components/content/FAQPage';
import Feedback from './feedback';
import TermsPage from './components/content/TermsPage'; // Add this import
import { responsiveFontSizes } from './theme/themesV3';

// Import theme utilities
import { useThemeColors, useThemeContext } from './theme/ThemeContext';
import {
  safeAlpha,
  getProfileBackgroundColor,
  getProfileHoverBackgroundColor,
  getAvatarBackgroundColor,
  getNavItemStyles,
  getButtonStyles,
  getCardStyles,
  getInputStyles,
} from './theme/themeUtils';

// Define Props interface
interface AppContentProps {
  urlPrimaryBase: string;
  getBusinessDates: (startDate: Date | string, count: number) => string[];
}

// Define types for state variables where needed
type AnchorElement = null | HTMLElement;

// Remove duplicate function declarations - now using imports from themeUtils

const AppContent: React.FC<AppContentProps> = ({
  urlPrimaryBase,
  getBusinessDates, 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { addStatus, removeStatus, statuses, clearAllStatuses } = useLoadingStatus();
  console.log('Current path:', currentPath);
  console.log('Current search params:', location.search);

  const jumpToTab = () => {
    const currentParams = new URLSearchParams(location.search);
    // Confirm that if the params has "view=dashboard" then the params should also include tab, profileId and profileSection, else set to this default - "tab=profiles&profileId=default&profileSection=analysis&view=dashboard"
    if ((currentParams.get('view') === 'dashboard') || currentParams.get('msg')) {
      let urlChanged = false;
      if (currentParams.get('view') === 'dashboard') {
        // Check if the params already include tab, profileId and profileSection
        if (!currentParams.has('tab')) {
          currentParams.set('tab', 'profiles'); 
          urlChanged = true;
        }
        if (!currentParams.has('profileId')) {
          currentParams.set('profileId', 'default');
          urlChanged = true;
        }
        if (!currentParams.has('profileSection')) {
          currentParams.set('profileSection', 'analysis');
          urlChanged = true;
        }
      } else if (currentParams.get('msg')) {
        addStatus(currentParams.get('msg') || ''); // Add status message if present
        currentParams.delete('msg'); // Remove msg param after adding status
        urlChanged = true;
      }

      if (urlChanged) {
        const newSearch = currentParams.toString();
        const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
        console.log('AppContent: Adding/Removing necessary params:', newUrl);
        navigate(newUrl, { replace: true }); // Use navigate replace
      }
    }
  };
  jumpToTab();

  // Use useMemo for searchParams for efficiency
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const currentTab = searchParams.get('tab');
  const currentProfileSection = searchParams.get('profileSection') || 'analysis';
  const currentView = searchParams.get('view'); // Get view param
  const currentTicker = searchParams.get('ticker'); // Get ticker param

  // Helper function to determine if navigation items should be highlighted
  const shouldHighlight = useMemo(() => {
    // Don't highlight anything when on profile page
    return currentPath !== '/profile';
  }, [currentPath]);

  // State variables with types
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<AnchorElement>(null);
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [searchTicker, setSearchTicker] = useState<string>('');
  const [loginMenuAnchorEl, setLoginMenuAnchorEl] = useState<AnchorElement>(null);
  const [profilesAnchorEl, setProfilesAnchorEl] = useState<AnchorElement>(null);
  const [mobileProfilesOpen, setMobileProfilesOpen] = useState<boolean>(false);
  const [marketReportFull, setMarketReportFull] = useState<MarketReportFull | null>(null); // Use MarketReport type
  const [bannerLoading, setBannerLoading] = useState<boolean>(false); // Keep for now, might be replaced by context
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [anchorReports, setAnchorReports] = useState<AnchorElement>(null);
  const [anchorAnalysis, setAnchorAnalysis] = useState<AnchorElement>(null);
  const [anchorTools, setAnchorTools] = useState<AnchorElement>(null);
  const [anchorMarketFactors, setAnchorMarketFactors] = useState<AnchorElement>(null);
  const [mobileReportsOpen, setMobileReportsOpen] = useState<boolean>(false);
  const [mobileAnalysisOpen, setMobileAnalysisOpen] = useState<boolean>(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState<boolean>(false);
  const [mobileMarketFactorsOpen, setMobileMarketFactorsOpen] = useState<boolean>(false);
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false);
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [showSubscriptionManager, setShowSubscriptionManager] = useState<boolean>(false);
  const [postLoginPreRefresh, setPostLoginPreRefresh] = useState<boolean>(false);
  const [tickerMap, setTickerMap] = useState<Record<string, string> | null>(null); // Initialize tickerMap



  // Get profile context instead of managing state locally
  const {
    profiles,
    currentProfile,
    currentProfileId,
    refreshProfiles,
    handleProfileSelect
  } = useProfile();

  // Derived state
  const open = Boolean(anchorEl);
  const isLoginMenuOpen = Boolean(loginMenuAnchorEl);

  // Hooks - Updated to include refreshToken
  const { user, isAuthenticated, token, login, loading: authLoading, logout, refreshToken } = useAuth();
  const [currentUserRole, setCurrentUserRole] = useState<string | undefined>(user?.role || (isAuthenticated ? 'unregistered' : undefined));
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  // Create a re-usable role-check function
  const hasRole = useCallback((requiredRoles: string | string[]) => {
    if (!currentUserRole) return false;
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.includes(currentUserRole);
    }
    return currentUserRole === requiredRoles;
  }, [currentUserRole]);

  // Create role check helpers for clarity
  const isPremium = useCallback(() => hasRole(['premium', 'premium_plus']), [hasRole]);
  const isBase = useCallback(() => hasRole(['base']), [hasRole]);
  const isFree = useCallback(() => hasRole(['free', 'unregistered']), [hasRole]); // Treat unregistered as free for disabling
  const isUnregistered = useCallback(() => hasRole(['unregistered']), [hasRole]); // Treat unregistered as free for disabling
  // Add helper for discovery access
  const canAccessDiscovery = useCallback(() => hasRole(['premium', 'premium_plus']), [hasRole]);
  ;

  // Effect to check for BFF authentication response on page load
  useEffect(() => {
    const checkBffAuthResponse = async () => {
      const params = new URLSearchParams(location.search);
      console.log('Checking BFF Pathname:', location.pathname);
      console.log('Checking BFF auth response...', params.toString());

      // Check for auth success indicator
      if (params.get('authSuccess') === 'true' && !postLoginPreRefresh) {
        console.log('BFF authentication successful, initializing session...');
        const authAction = params.get('authAction'); // Read the action context
        console.log('Auth Action Context:', authAction);

        // Define cleanup logic
        const cleanupUrl = () => {
          const currentParams = new URLSearchParams(location.search);
          currentParams.delete('authSuccess');
          currentParams.delete('sessionId');
          currentParams.delete('token');
          currentParams.delete('authAction');
          const newSearch = currentParams.toString();
          const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
          console.log('Cleaning URL to:', newUrl);
          navigate(newUrl, { replace: true }); // Use navigate replace
        };

        try {
          const sessionId = params.get('sessionId');
          const tokenParam = params.get('token');

          if (!sessionId) {
            console.error('No sessionId provided in BFF auth success redirect');
            cleanupUrl(); // Clean URL even if session ID is missing
            return;
          }
          // Clean URL *before* potentially setting state that depends on authAction
          setPostLoginPreRefresh(true); // Set flag to indicate pre-refresh state
          cleanupUrl();
          // Call refreshToken to initialize user data
          const refreshSuccess = await refreshToken(sessionId, tokenParam ?? undefined, authAction === 'register');

          setPostLoginPreRefresh(false); // Reset flag after refreshToken call

          if (!refreshSuccess) {
            console.error('Failed to initialize user data after BFF authentication');
          } else {
            console.log('BFF authentication complete with user data');

            // Restore state based on authAction AFTER cleaning URL and refreshing token
            // This state update will be applied in the re-render triggered by navigate()
            if (authAction === 'register') {
              console.log('Restoring registration workflow state.');
              setShowRegistration(true);
            } else if (authAction === 'manageSubscription') {
               console.log('Restoring subscription manager state.');
               setShowSubscriptionManager(true);
            }
            // Add more else if blocks for other contexts if needed
          }
        } catch (err) {
          console.error('Error completing BFF authentication:', err);
          cleanupUrl(); // Attempt to clean URL even on error
        } finally {
          // Any cleanup if needed
        }
      }

      // Check for auth cancelled indicator
      if (params.get('authCancelled') === 'true' && !postLoginPreRefresh) {
        console.log('BFF authentication was cancelled');
        const authAction = params.get('authAction'); // Read context even on cancel

        // Clean URL parameters first
        const cleanParams = new URLSearchParams(location.search);
        cleanParams.delete('authCancelled');
        cleanParams.delete('authAction');
        const newSearch = cleanParams.toString();
        const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
        navigate(newUrl, { replace: true }); // Use navigate replace

        // Optionally restore state even on cancel if needed, AFTER cleaning URL
        // This state update will be applied in the re-render triggered by navigate()
        if (authAction === 'register') {
           setShowRegistration(true); // Re-open modal so user sees the cancellation error within it
        } else if (authAction === 'manageSubscription') {
           setShowSubscriptionManager(true);
        }
        // Error is likely set in AuthContext, no need to set it here unless specific message needed
      }
    };

    checkBffAuthResponse();
    // Ensure setters are included in dependencies if they might change, though unlikely for top-level state setters
    // Keep navigate in dependencies as we now use it inside the effect.
  }, [location.search, refreshToken, navigate, postLoginPreRefresh]);

  // Helper function for navigation that preserves existing search parameters
  const navigateWithParams = useCallback((path: string, 
    newParams: Record<string, string | number | boolean | null | undefined> = {}, 
    options: { replace?: boolean; state?: any } = {}) => {
    const currentSearchParams = new URLSearchParams(location.search);
    const finalParams = new URLSearchParams();

    // Copy existing params first
    currentSearchParams.forEach((value, key) => {
      finalParams.set(key, value);
    });

    // Override or add new params
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        finalParams.delete(key); // Allow removing params by setting value to null/undefined
      } else {
        finalParams.set(key, String(value));
      }
    });

    const queryString = finalParams.toString();
    // Ensure path starts with a slash if it's not empty
    const navigatePath = path.startsWith('/') || path === '' ? path : `/${path}`;
    navigate(`${navigatePath}${queryString ? `?${queryString}` : ''}`, options);
  }, [navigate, location.search]);

  // Build loginId from user object for backward compatibility
  const loginId: (string | number | undefined)[] | null = user ? [
    user.loginId,
    user.userName,
    user.smaName || '',
    user.smaSince || '',
    user.level,
    user.picture || ''
  ] : null;
  console.log("AppContent: loginId:", loginId);

  // Effect for handling user role changes
  useEffect(() => {
    if (user?.role && user.role !== currentUserRole) {
      console.log('User role changed:', user.role);
      setCurrentUserRole(user.role);
      // if (isAuthenticated && token && !isUnregistered()) {
      //   // setTimeout(() => {
      //     console.log('Refreshing profiles... in NewApp post login');
      //     refreshProfiles();
      //   // }, 100);
      // }
    } else if (!user && currentUserRole && currentUserRole !== 'unregistered') {
      console.log('User logged out, clearing role', user, currentUserRole);
      setCurrentUserRole(undefined); // Use undefined for cleared role
    }
  }, [user]); // Removed user.role as it's covered by user

  // Effect for loading profiles when authenticated
  useEffect(() => {
    if (isAuthenticated && token && !isUnregistered()) {
      setTimeout(() => {
        console.log('Refreshing profiles... in NewApp post login');
        refreshProfiles();
      }, 100);
    }
  }, [currentUserRole]);

  // Effect to fetch MarketReport for the banner using getMarketReport

  // Get loading status functions for API
  // Create API instance with loading functions
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []); // Added dependencies

  // Clean up statuses when component unmounts
  useEffect(() => {
    return () => {
      console.log('AppContent unmounting, clearing all statuses');
      clearAllStatuses();
    };
  }, [clearAllStatuses]);

  useEffect(() => {
    const fetchBannerData = async () => {
      // Fetch only when logged in and a profile is selected
      if (isAuthenticated && currentProfile?.id) {
        console.log("AppContent: Fetching MarketReport for banner using getMarketReport...");
        // setBannerLoading(true); // Status handled by API call now
        setBannerError(null);
        setMarketReportFull(null); // Clear previous data
        try {
          // Use the api from context
          const data = await api.getMarketReport(currentProfile.investment_profile_template);
          setMarketReportFull(data);
          if (isPremium()) {
            const tickerMap = await api.getTickerMap(); // Fetch ticker map if user is premium
            console.log("AppContent: Ticker map fetched successfully:", tickerMap);
            setTickerMap(tickerMap); // Set ticker map in state
          }
          console.log("AppContent: MarketReport fetched successfully for banner.", data);
        } catch (err: any) { // Catch error as any
          console.error("AppContent: Error fetching MarketReport for banner:", err);
          setBannerError(err.message || 'Failed to load market data');
          setMarketReportFull(null); // Clear data on error
        } finally {
          // setBannerLoading(false); // Status handled by API call now
        }
      } else {
        // Clear banner data if not authenticated or no profile selected
        setMarketReportFull(null);
        // setBannerLoading(false); // Status handled by API call now
        setBannerError(null);
      }
    };

    fetchBannerData();
  // Depend on isAuthenticated and currentProfile.id, and api instance
  }, [isAuthenticated, currentProfile?.id, api]); // Removed currentProfile?.investment_profile_template

  // Add scroll event listener to track when to show the scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 300px from the top
      if (window.pageYOffset > 300) {
        setShowScrollToTop(true);
      } else {
        setShowScrollToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handler functions
  const handleDrawerToggle = () => {
    // Only toggle mobileOpen
    setMobileOpen(!mobileOpen);
    // Close mobile profiles menu when drawer closes
    if (mobileOpen) {
      setMobileProfilesOpen(false);
    }
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Update handleSubTabChange to ensure it clears view/ticker when changing main sections
  const handleSubTabChange = (newValue: string) => {
    // Use navigateWithParams, navigate to /premium, set profileSection, clear view/ticker
    navigateWithParams('/premium', { tab: 'profiles', profileId: currentProfileId, profileSection: newValue, view: null, ticker: null });
  };

  // Define handleSubTabChange before it's used in handleSubTabChangeAndClose
  const handleMarketAnalysisViewChange = (view?: string) => { // Make view optional
    // Use navigateWithParams, navigate to /premium, set section and view
    // Default to 'dashboard' if view is not specified
    const finalView = view || 'dashboard';
    navigateWithParams('/premium', { tab: 'profiles', profileId: currentProfileId, profileSection: 'analysis', view: finalView, ticker: null });
  };

  // Handlers for nested Profile Menu
  const handleProfilesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfilesAnchorEl(event.currentTarget);
  };

  const handleProfilesMenuClose = () => {
    setProfilesAnchorEl(null);
  };

  const handleProfileSelectAndClose = (profileId: string) => {
    // handleProfileSelect in context now handles navigation preserving params
    handleProfileSelect(profileId);
    setProfilesAnchorEl(null); // Close only the profiles menu
    // Also close mobile drawer if open
    if (mobileOpen) {
        handleDrawerToggle();
        setMobileProfilesOpen(false); // Ensure mobile sub-menu closes
    }
  };

  const handleNavigateAndClose = (path: string, state: any = {}, newParams: Record<string, string | number | boolean | null | undefined> = {}) => {
    // Use navigateWithParams, passing along state and any new params
    navigateWithParams(path, newParams, { state });
  };

  // Now calls the defined handleSubTabChange
  const handleSubTabChangeAndClose = (newValue: string) => {
    handleSubTabChange(newValue);
  };

  // Updated function to handle ticker resolution and navigation
  const resolveAndNavigateTickerSearch = (searchValue: string | null) => {
    if (!searchValue) return;

    const trimmedValue = searchValue.trim();
    if (!trimmedValue) return;

    let resolvedTicker: string | undefined = undefined;

    // Attempt 1: searchValue is a full label from tickerMap keys
    if (tickerMap && tickerMap[trimmedValue]) {
      resolvedTicker = tickerMap[trimmedValue];
    }

    // Attempt 2: searchValue is a ticker symbol (a value in tickerMap)
    if (!resolvedTicker && tickerMap) {
      const upperValue = trimmedValue.toUpperCase();
      // Check if upperValue is one of the ticker symbols in the map
      for (const key in tickerMap) {
        if (tickerMap[key] === upperValue) {
          resolvedTicker = upperValue;
          break;
        }
      }
    }

    // Attempt 3: Fallback - treat the input as a ticker symbol directly
    // This covers cases where user types a ticker not in map, or if tickerMap is null
    if (!resolvedTicker) {
      resolvedTicker = trimmedValue.toUpperCase();
    }

    if (resolvedTicker) {
      const currentParams = new URLSearchParams(location.search);
      const paramsToSend: Record<string, string | null> = {
        view: 'ticker',
        ticker: resolvedTicker,
      };

      // Preserve profile context if user is currently within it
      if (currentParams.get('tab') === 'profiles' && currentParams.get('profileId')) {
        paramsToSend.tab = 'profiles';
        paramsToSend.profileId = currentParams.get('profileId');
        // paramsToSend.profileSection = currentParams.get('profileSection'); // Optional: reset or preserve
      } else {
        // If not in profile context, clear profile params
        paramsToSend.tab = null;
        paramsToSend.profileId = null;
        paramsToSend.profileSection = null;
      }

      navigateWithParams('/premium', paramsToSend);
      setSearchTicker(''); // Clear the search input
      // Close mobile drawer if it was the source of the search and is open
      if (mobileOpen) {
        handleDrawerToggle();
      }
    }
  };


  // Define onBack handler for TickerDetailView
  const handleTickerDetailBack = useCallback(() => {
    console.log('Back button clicked in TickerDetailView');
    const params = new URLSearchParams(location.search);
    params.set('view', 'dashboard'); // Set view to dashboard
    params.delete('ticker'); // Clear ticker param
    // Navigate back to the previous state defined by the remaining params
    console.log('Navigated back to dashboard view:', `${location.pathname}?${params.toString()}`);
    navigate(`${location.pathname}?${params.toString()}`);
  }, [navigate, location]);


  // Handler for login menu
  const handleLoginMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setLoginMenuAnchorEl(event.currentTarget);
  };

  const handleLoginMenuClose = () => {
    setLoginMenuAnchorEl(null);
  };

  const handleLoginWithProvider = (provider: 'google' | 'twitter') => {
    handleLoginMenuClose();
    handleLogin(provider);
  };

  const handleLogin = async (provider: 'google' | 'twitter') => {
    try {
      console.log(`Initiating login with ${provider}...`);

      if (typeof login !== 'function') {
        console.error('login is not a function:', login);
        return;
      }

        //       // Popup blocker detection
        // const testPopup = window.open('about:blank', '_blank', 'width=100,height=100');
        // if (!testPopup || testPopup.closed || typeof testPopup.closed === 'undefined') {
        //   alert("Please allow popups for this site to login.");
        //   return;
        // }
        // testPopup.close();

      // Use BFF by default now
      const useBff = true;
      await login(provider, useBff);

      console.log(`Login attempt with ${provider} completed.`);
    } catch (error: any) { // Catch error as any
      console.error(`${provider} login failed:`, error);
      alert(`Login failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handlePlanChange = (newRole: string) => {
    console.log('Plan changed to:', newRole);
    setCurrentUserRole(newRole);
    // Clear cache on plan change
    clearCache();
    logout(); // Call logout to clear session
    console.log('Cache cleared on plan change.');

    // Force a full refresh to ensure the app reflects the new role
    setTimeout(() => navigate('/'), 500);
  };

  // Handler for ticker selection from banner
  const handleBannerTickerSelect = useCallback((ticker: string) => {
    console.log('Banner ticker selected:', ticker);
    // Use navigateWithParams to go to premium page with view=ticker
    const currentParams = new URLSearchParams(location.search);
    const paramsToSend: Record<string, string | null> = { // Define type for paramsToSend
        view: 'ticker',
        ticker: ticker
    };
    // Preserve profile context if user is currently within it
    if (currentParams.get('tab') === 'profiles' && currentParams.get('profileId')) {
        paramsToSend.tab = 'profiles';
        paramsToSend.profileId = currentParams.get('profileId');
    } else {
         paramsToSend.tab = null;
         paramsToSend.profileId = null;
         paramsToSend.profileSection = null;
    }
    navigateWithParams('/premium', paramsToSend);
  }, [navigateWithParams, location.search]);

  // Define banner height (adjust as needed based on TickerBanner's actual height)
  const BANNER_HEIGHT = 36; // In pixels

  // Scroll to top button
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Type guard for User
  function isUser(obj: any): obj is User {
    return obj && typeof obj.userName === 'string';
  }

  const autocompleteOptions = useMemo(() => (tickerMap ? Object.keys(tickerMap) : []), [tickerMap]);

  // Helper functions for styling based on state
  const getBackgroundColor = (isSelected: boolean, theme: Theme): string => {
    return getProfileBackgroundColor(isSelected, theme);
  };

  const getHoverBackgroundColor = (isSelected: boolean, theme: Theme): string => {
    return getProfileHoverBackgroundColor(isSelected, theme);
  };

  // Use theme colors hook and full theme context
  const themeColors = useThemeColors();
  const { theme } = useThemeContext(); // Get the full theme object

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      {/* Browser Redirect Banner - Must be first */}
      <BrowserRedirectBanner />
      {/* Ticker Banner - Always at the top of the page above header */}
      {/* Show for registered users on all pages OR unregistered users on home page */}
      {(isAuthenticated && (currentUserRole === 'free' || currentUserRole === 'base' || currentUserRole === 'premium' || currentUserRole === 'premium_plus')) ||
        (!isAuthenticated && currentPath === '/') ? (
        <Box sx={{
            width: '100%',
            position: 'static', // Changed from fixed to static
            backgroundColor: (theme) => theme.palette.mode === 'dark' ?
              theme.palette.background.paper :
              theme.palette.primary.main,
            color: (theme) => theme.palette.mode === 'dark' ?
              theme.palette.text.primary :
              theme.palette.primary.contrastText,
            boxShadow: 'none',
            zIndex: 1300 // Ensure it's above everything else
           }}
        >
          <TickerBanner
            bannerData={
              // Use marketReportFull for registered users, hardcoded example data for unregistered
              isAuthenticated ?
                marketReportFull?.market_report?.tickers_for_banner || null :
                {
                  // Example default tickers for unregistered users
                  MarketETFs: [],
                  SectorETFs: [],
                  SelectedTickers: [
                    // { ticker: 'SPY', close: 470.25, change: 0.85 },
                    // { ticker: 'QQQ', close: 380.10, change: 1.20 },
                    // { ticker: 'AAPL', close: 175.50, change: -0.75 },
                    // { ticker: 'MSFT', close: 325.20, change: 1.50 },
                    // { ticker: 'AMZN', close: 135.75, change: 2.10 },
                    // { ticker: 'GOOGL', close: 125.80, change: 0.30 }
                  ]
                }
            }
            onTickerSelect={handleBannerTickerSelect}
            isLoading={false} // Use loading status context
            error={isAuthenticated ? bannerError : null}
          />
        </Box>
      ) : null}

      {/* App Bar - Adjusted to be static instead of fixed */}
      <AppBar
        position="static" // Changed from fixed to static
        sx={{
          // Updated to use background.default to match the page background
          bgcolor: (theme) => theme.palette.background.default,
          color: (theme) => theme.palette.text.primary,
          boxShadow: 'none'
        }}
        color="default"
      >
        <Toolbar sx={{ display: 'flex', flexDirection: 'column', py: 1, px: { xs: 1, sm: 2 } }}>
          {/* Top Row: Logo, Profile Dropdown, Search, User Menu */}
          <Box sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            mb: 1.5,
            justifyContent: 'space-between'
          }}>
            {/* Left side: Logo + Mobile Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Mobile Menu Icon */}
              <IconButton
                //color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 1, 
                  color: theme.palette.custom.iconPrimary,
                  display: { md: 'none' } }} // Show only on mobile
              >
                <MenuIcon />
              </IconButton>

              {/* Logo - keep on all screen sizes */}
              <Typography
                variant="h6"
                noWrap
                component="div"
                onClick={() => navigateWithParams('/')} // Use navigateWithParams
                sx={{
                  fontWeight: 'bold',
                  minWidth: 50,
                  mr: 2,
                  cursor: 'pointer',
                  // Use primary color in light mode and info color in dark mode
                  color: (theme) => theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SMAIcon sx={{
                  mr: 1,
                  color: (theme) => theme.palette.mode === 'dark' ? theme.palette.custom.iconPrimary : theme.palette.custom.iconPrimary
                }} /> SMA
              </Typography>
            </Box>

            {/* Navigation Links for Unregistered Users */}
            {!isAuthenticated && (
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center', // Center the links horizontally
                flexGrow: 1, // Take up available space
                px: 2 // Add some padding
              }}>
                <Button
                  color="inherit"
                  onClick={() => navigateWithParams('/')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: currentPath === '/' ? 600 : 400,
                    borderRadius: '8px',
                    mx: 1, // Use mx instead of mr for even spacing
                    color: (theme) => theme.palette.mode === 'dark' ?
                      (currentPath === '/' ? theme.palette.info.main : theme.palette.text.primary) :
                      (currentPath === '/' ? theme.palette.primary.main : theme.palette.text.primary),
                    '&:hover': {
                      backgroundColor: (theme) => alpha(
                        theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.primary.main,
                        0.08
                      )
                    }
                  }}
                >
                  Home
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigateWithParams('/about')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: currentPath === '/about' ? 600 : 400,
                    borderRadius: '8px',
                    mx: 1, // Use mx instead of mr for even spacing
                    color: (theme) => theme.palette.mode === 'dark' ?
                      (currentPath === '/about' ? theme.palette.info.main : theme.palette.text.primary) :
                      (currentPath === '/about' ? theme.palette.primary.main : theme.palette.text.primary),
                    '&:hover': {
                      backgroundColor: (theme) => alpha(
                        theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.primary.main,
                        0.08
                      )
                    }
                  }}
                >
                  About Us
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigateWithParams('/faq')}
                  sx={{
                    textTransform: 'none',
                    fontWeight: currentPath === '/faq' ? 600 : 400,
                    borderRadius: '8px',
                    mx: 1, // Use mx instead of mr for even spacing
                    color: (theme) => theme.palette.mode === 'dark' ?
                      (currentPath === '/faq' ? theme.palette.info.main : theme.palette.text.primary) :
                      (currentPath === '/faq' ? theme.palette.primary.main : theme.palette.text.primary),
                    '&:hover': {
                      backgroundColor: (theme) => alpha(
                        theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.primary.main,
                        0.08
                      )
                    }
                  }}
                >
                  FAQ
                </Button>
              </Box>
            )}

            {/* Center: Profile Selector - Styled like "Taskie" in the inspiration */}
            {isAuthenticated && (currentUserRole === 'free' || currentUserRole === 'base' || currentUserRole === 'premium' || currentUserRole === 'premium_plus') && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2
                }}
              >
                {/* Profile Selector */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    // Use theme background paper instead of default
                    backgroundColor: (theme) => theme.palette.background.paper,
                    borderRadius: '28px',
                    border: 1,
                    borderColor: (theme) => theme.palette.divider,
                    px: 2,
                    py: 1.5,
                    height: 48,
                    width: { sm: 220, md: 280 },
                    // Updated shadow using theme's custom border color
                    boxShadow: (theme) => `0px 2px 4px ${safeAlpha(
                      theme.palette.custom?.borderColor, 
                      0.2,
                      theme.palette.divider
                    )}`
                  }}
                >
                  <Box
                    onClick={handleProfilesMenuOpen}
                    sx={{
                      cursor: isPremium() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      flexDirection: 'column',
                      opacity: isPremium() ? 1 : 0.6,
                      width: '100%'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: 'calc(100% - 24px)', overflow: 'hidden' }}>
                        <Typography fontWeight="medium" variant="subtitle1" noWrap>
                          {currentProfile?.name || "Select Profile"}
                        </Typography>
                        {currentProfile?.investment_profile_template && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{
                              ml: 1,
                              display: { xs: 'none', sm: 'block' }
                            }}
                          >
                            • {currentProfile.investment_profile_template.split('-').map(word =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </Typography>
                        )}
                      </Box>
                      {isPremium() && <ExpandMoreIcon />}
                    </Box>
                  </Box>
                </Box>

                {/* Search Box - Now styled as a rounded pill and placed next to profile selector */}
                <Paper
                  elevation={1}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: { sm: 220, md: 280 },
                    height: 48,
                    borderRadius: '28px',
                    border: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    overflow: 'hidden',
                    transition: 'box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: (theme) => isPremium() ? 
                        `0px 2px 8px ${safeAlpha(
                          theme.palette.custom?.borderColor, 
                          0.3,
                          theme.palette.primary.main
                        )}` : 'none'
                    },
                    opacity: isPremium() ? 1 : 0.6, // Apply opacity to the whole Paper
                  }}
                >
                  <SearchIconMui  sx={{ color: theme.palette.custom.iconPrimary, fontSize: '1.25rem', ml: 2, mr: 1 }} />
                  <Autocomplete
                    freeSolo
                    fullWidth
                    options={autocompleteOptions}
                    value={searchTicker}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === 'input') {
                        setSearchTicker(newInputValue);
                      } else if (reason === 'clear') {
                        setSearchTicker('');
                      }
                      // If an option is selected, 'reset' reason might occur.
                      // We handle selection via onChange, so setSearchTicker('') there is preferred.
                    }}
                    onChange={(event, newValue) => { // newValue is string | null
                      resolveAndNavigateTickerSearch(newValue);
                    }}
                    disabled={!isPremium()}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': { // Targets the TextField's root inside Autocomplete
                        height: 48,
                        paddingLeft: '0px !important', // Remove Autocomplete's default left padding for input
                         '& .MuiAutocomplete-input': { // Targets the actual input element
                           padding: '12.5px 4px 12.5px 0px', // Fine-tune padding for the input text
                         },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search ticker or company..."
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { border: 'none' },
                            '&:hover fieldset': { border: 'none' },
                            '&.Mui-focused fieldset': { border: 'none' },
                            height: 48, // Ensure TextField root maintains height
                          },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          disableUnderline: true,
                        }}
                      />
                    )}
                  />
                </Paper>
              </Box>
            )}

            {/* Right Side: User Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Theme Toggle */}
              <ThemeToggle sx={{ marginRight: 1 }} /> 
              <Feedback/>

              {/* User Menu / Login Buttons */}
              {loginId ? ( // Check if user is logged in
                <>
                  <IconButton
                    size="small"
                    onClick={handleAvatarClick}
                    sx={{
                      border: currentPath === '/profile' ? 2 : 1,
                      // Use theme colors for border highlight
                      borderColor: currentPath === '/profile' ? 'primary.main' : 'divider',
                      borderRadius: '50%',
                      
                      p: 0.5,
                      // Updated shadow using theme with transition
                      boxShadow: (theme) => `0px 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        boxShadow: (theme) => `0px 3px 6px ${alpha(theme.palette.primary.main, 0.2)}`,
                      }
                    }}
                  >
                    {user?.picture ? (
                      <Avatar
                        src={user.picture}
                        sx={{ width: 32, height: 32 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                        {user?.userName?.[0]?.toUpperCase() || 'U'}
                      </Avatar>
                    )}
                  </IconButton>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleLoginMenuClick}
                    variant="outlined"
                    size="small"
                    color="primary"
                    sx={{
                      ...getButtonStyles('outlined', theme), // Use full theme object
                      mr: 1,
                      display: { xs: 'none', sm: 'inline-flex' },
                      fontWeight: 500,
                    }}
                  >
                    Login
                  </Button>

                  <Button
                    onClick={() => setShowRegistration(true)}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      ...getButtonStyles('contained', theme), // Use full theme object
                      display: { xs: 'none', sm: 'inline-flex' },
                      fontWeight: 500,
                      bgcolor: (theme) => theme.palette.primary.main,
                      color: (theme) => theme.palette.primary.contrastText
                    }}
                  >
                    Register
                  </Button>

                  {/* Simplified mobile buttons */}
                  <Button
                    onClick={handleLoginMenuClick}
                    variant="text"
                    color="primary"
                    size="small"
                    sx={{
                      minWidth: 0,
                      px: 1,
                      display: { xs: 'inline-flex', sm: 'none' }
                    }}
                  >
                    Login
                  </Button>
                  
                  {/* Add Register button for mobile */}
                  <Button
                    onClick={() => setShowRegistration(true)}
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      minWidth: 0,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '16px',
                      display: { xs: 'inline-flex', sm: 'none' },
                      fontSize: '0.75rem',
                      ml: 1
                    }}
                  >
                    Register
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Bottom Row: Pillbox Navigation for Market Analysis, Watchlists, Screeners, etc. */}
          {isAuthenticated && (currentUserRole === 'free' || currentUserRole === 'base' || currentUserRole === 'premium' || currentUserRole === 'premium_plus') && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                mt: 0.5,
                overflowX: 'auto'
              }}
            >
              <Box sx={{
                display: 'flex',
                // Use theme colors
                bgcolor: (theme) => theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.custom.navBg, 0.8) 
                  : alpha(theme.palette.custom.navBg, 0.8),
                borderRadius: 28,
                p: 0.75,
                boxShadow: (theme) => `0px 2px 6px ${alpha(theme.palette.primary.main, 0.08)}`,
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
              }}>
                {/* Simplified Navigation - Consolidated Categories */}
                <Button
                  onClick={() => handleMarketAnalysisViewChange('dashboard')}
                  disabled={!currentProfileId}
                  variant={shouldHighlight && currentProfileSection === 'analysis' && searchParams.get('view') === 'dashboard' ? 'contained' : 'text'}
                  startIcon={<DashboardIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                  sx={{
                    ...getButtonStyles(
                      shouldHighlight && currentProfileSection === 'analysis' && searchParams.get('view') === 'dashboard' ? 'contained' : 'text',
                      theme // Use full theme object
                    ),
                    mx: 0.5,
                    px: 2,
                    py: 1,
                    opacity: !currentProfileId ? 0.6 : 1,
                  }}
                >
                  Dashboard
                </Button>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant={shouldHighlight && currentProfileSection === 'analysis' && ['questions', 'recommendations', 'insights'].includes(searchParams.get('view') ?? '') ? 'contained' : 'text'}
                    onClick={(event) => setAnchorReports(event.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                    startIcon={<Description sx={{color: theme.palette.custom.iconPrimary}}/>}
                    sx={{
                      ...getButtonStyles(
                        shouldHighlight && currentProfileSection === 'analysis' && ['questions', 'recommendations', 'insights'].includes(searchParams.get('view') ?? '') ? 'contained' : 'text',
                        theme // Use full theme object
                      ),
                      mx: 0.5,
                      px: 2,
                      py: 1,
                      opacity: (!(isPremium() || isBase()) || !currentProfileId) ? 0.6 : 1,
                    }}
                    disabled={!(isPremium() || isBase()) || !currentProfileId}
                  >
                    Reports 
                  </Button>
                  <Menu
                    anchorEl={anchorReports}
                    open={Boolean(anchorReports)}
                    onClose={() => setAnchorReports(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        mt: 1,
                        borderRadius: 2,
                        p: 1,
                        // Enhanced menu styling using theme
                        boxShadow: (theme) => `0px 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
                      }
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('questions');
                        setAnchorReports(null);
                      }}
                      selected={searchParams.get('view') === 'questions'}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        // Enhanced menu item styling with theme
                        transition: 'background-color 0.2s ease-in-out',
                        bgcolor: searchParams.get('view') === 'questions' ?
                          (theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: theme.palette.custom.iconPrimary,
                        // Use theme's custom icon colors
                        // color: searchParams.get('view') === 'questions' ?
                        //   (theme) => theme.palette.mode === 'dark' ?
                        //     theme.palette?.custom?.iconPrimary:
                        //     theme.palette?.custom?.iconPrimary
                        //   : (theme) => 'inherit'
                      }}>
                        <QuestionAnswerIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Key Questions" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('recommendations');
                        setAnchorReports(null);
                      }}
                      selected={searchParams.get('view') === 'recommendations'}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        transition: 'background-color 0.2s ease-in-out'
                      }}
                    >
                      <ListItemIcon><RecommendationsIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Recommendations" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('insights');
                        setAnchorReports(null);
                      }}
                      selected={searchParams.get('view') === 'insights'}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        transition: 'background-color 0.2s ease-in-out'
                      }}
                    >
                      <ListItemIcon><AssignmentTurnedIn sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Complete Report" />
                    </MenuItem>
                  </Menu>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant={shouldHighlight && currentProfileSection === 'analysis' && ['sectors', 'market-caps'].includes(searchParams.get('view') ?? '') ? 'contained' : 'text'}
                    onClick={(event) => setAnchorAnalysis(event.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                    startIcon={<AutoGraph sx={{color: theme.palette.custom.iconPrimary}}/>}
                    sx={{
                      ...getButtonStyles(
                        shouldHighlight && currentProfileSection === 'analysis' && ['sectors', 'market-caps'].includes(searchParams.get('view') ?? '') ? 'contained' : 'text',
                        theme // Use full theme object
                      ),
                      mx: 0.5,
                      px: 2,
                      py: 1,
                      opacity: (!(isPremium() || isBase()) || !currentProfileId) ? 0.6 : 1,
                    }}
                    disabled={!(isPremium() || isBase()) || !currentProfileId}
                  >
                    Analysis
                  </Button>
                  <Menu
                    anchorEl={anchorAnalysis}
                    open={Boolean(anchorAnalysis)}
                    onClose={() => setAnchorAnalysis(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        mt: 1,
                        borderRadius: 2,
                        p: 1
                      }
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('sectors');
                        setAnchorAnalysis(null);
                      }}
                      selected={searchParams.get('view') === 'sectors'}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemIcon><SectorIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Sector Analysis" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('market-caps');
                        setAnchorAnalysis(null);
                      }}
                      selected={searchParams.get('view') === 'market-caps'}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemIcon><MarketCapIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Marketcap Analysis" />
                    </MenuItem>
                  </Menu>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant={shouldHighlight && currentProfileSection === 'analysis' && ['performance', 'risk-level', 'money-flow'].includes(searchParams.get('view') ?? '') ? 'contained' : 'text'}
                    onClick={(event) => setAnchorMarketFactors(event.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                    startIcon={<Tune sx={{color: theme.palette.custom.iconPrimary}}/>}
                    sx={{
                      ...getButtonStyles(
                        shouldHighlight && currentProfileSection === 'analysis' && ['performance', 'risk-level', 'money-flow'].includes(searchParams.get('view') ?? '') ? 'contained' : 'text',
                        theme // Use full theme object
                      ),
                      mx: 0.5,
                      px: 2,
                      py: 1,
                      opacity: (!(isPremium() || isBase()) || !currentProfileId) ? 0.6 : 1,
                    }}
                    disabled={!(isPremium() || isBase()) || !currentProfileId}
                  >
                    Market Factors
                  </Button>
                  <Menu
                    anchorEl={anchorMarketFactors}
                    open={Boolean(anchorMarketFactors)}
                    onClose={() => setAnchorMarketFactors(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        mt: 1,
                        borderRadius: 2,
                        p: 1
                      }
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('performance');
                        setAnchorMarketFactors(null);
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        // Use a direct reference to the theme in a single function
                        bgcolor: (theme: Theme) => 
                          searchParams.get('view') === 'performance' 
                            ? theme.palette.mode === 'dark'
                              ? alpha(theme.palette.info.main, 0.12)
                              : alpha(theme.palette.primary.light, 0.5)
                            : 'transparent'
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: theme.palette.custom.iconPrimary,
                        // color: searchParams.get('view') === 'performance' ?
                        //   (theme: Theme) => theme.palette.mode === 'dark' ?
                        //     theme.palette.info.main :
                        //     theme.palette.primary.main
                        //   : 'inherit'
                      }}>
                        <TrendingUpIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Performance"
                        primaryTypographyProps={{
                          sx: {
                            color: searchParams.get('view') === 'performance' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('risk-level');
                        setAnchorMarketFactors(null);
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        bgcolor: searchParams.get('view') === 'risk-level' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: theme.palette.custom.iconPrimary,
                        // color: searchParams.get('view') === 'risk-level' ?
                        //   (theme: Theme) => theme.palette.mode === 'dark' ?
                        //     theme.palette.info.main :
                        //     theme.palette.primary.main
                        //   : 'inherit'
                      }}>
                        <WarningIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Risk Level"
                        primaryTypographyProps={{
                          sx: {
                            color: searchParams.get('view') === 'risk-level' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleMarketAnalysisViewChange('money-flow');
                        setAnchorMarketFactors(null);
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        bgcolor: searchParams.get('view') === 'money-flow' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: theme.palette.custom.iconPrimary,
                        // color: searchParams.get('view') === 'money-flow' ?
                        //   (theme: Theme) => theme.palette.mode === 'dark' ?
                        //     theme.palette.info.main :
                        //     theme.palette.primary.main
                        //   : 'inherit'
                      }}>
                        <MonetizationOnIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Money Flow"
                        primaryTypographyProps={{
                          sx: {
                            color: searchParams.get('view') === 'money-flow' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </MenuItem>
                  </Menu>
                </Box>

                <Divider sx={{ 
                  my: 1,
                  borderColor: (theme) => theme.palette?.custom?.borderColor || theme.palette.divider
                }} />

                <Button
                  onClick={() => handleSubTabChange('watchlists')}
                    disabled={!isPremium() || !currentProfileId}
                  variant={shouldHighlight && currentProfileSection === 'watchlists' ? 'contained' : 'text'}
                  startIcon={<WatchlistIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                    sx={{
                    ...getButtonStyles(
                      shouldHighlight && currentProfileSection === 'watchlists' ? 'contained' : 'text',
                      theme // Use full theme object
                    ),
                    mx: 0.5,
                    px: 2,
                    py: 1,
                      opacity: !isPremium() || !currentProfileId ? 0.6 : 1,
                    }}
                  >
                  Watchlists
                </Button>

                <Button
                  onClick={() => handleSubTabChange('screeners')}
                    disabled={!isPremium() || !currentProfileId}
                  variant={shouldHighlight && currentProfileSection === 'screeners' ? 'contained' : 'text'}
                  startIcon={<ScreenerIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                    sx={{
                    ...getButtonStyles(
                      shouldHighlight && currentProfileSection === 'screeners' ? 'contained' : 'text',
                      theme // Use full theme object
                    ),
                    mx: 0.5,
                    px: 2,
                    py: 1,
                      opacity: !isPremium() || !currentProfileId ? 0.6 : 1,
                    }}
                  >
                  Screeners
                </Button>

                <Divider sx={{ 
                  my: 1,
                  borderColor: (theme) => theme.palette?.custom?.borderColor || theme.palette.divider
                }} />

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button
                    variant={currentPath === '/tools' ? 'contained' : 'text'}
                    onClick={(event) => setAnchorTools(event.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                    startIcon={<ToolsIcon sx={{color: theme.palette.custom.iconPrimary}}/>}
                sx={{
                      ...getButtonStyles(
                        currentPath === '/tools' ? 'contained' : 'text',
                        theme // Use full theme object
                      ),
                      mx: 0.5,
                      px: 2,
                      py: 1,
                  opacity: !isAuthenticated ? 0.6 : 1,
                }}
                    disabled={!isAuthenticated}
                  >
                    Tools
                  </Button>
                  <Menu
                    anchorEl={anchorTools}
                    open={Boolean(anchorTools)}
                    onClose={() => setAnchorTools(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        mt: 1,
                        borderRadius: 2,
                        p: 1
                      }
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        navigateWithParams('/tools', { section: 'market-etfs' });
                        setAnchorTools(null);
                      }}
                      selected={currentPath === '/tools' && searchParams.get('section') === 'market-etfs'}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemIcon><MarketCapIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Market ETFs Analysis" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigateWithParams('/tools', { section: 'sector-etfs' });
                        setAnchorTools(null);
                      }}
                      selected={currentPath === '/tools' && searchParams.get('section') === 'sector-etfs'}
                      disabled={isFree()}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemIcon><SectorIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Sector ETFs Analysis" />
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigateWithParams('/tools', { section: 'discovery' });
                        setAnchorTools(null);
                      }}
                      selected={currentPath === '/tools' && searchParams.get('section') === 'discovery'}
                      disabled= {!canAccessDiscovery()}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemIcon><StockDiscoveryIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Stock Discovery" />
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Box>
            )}
          </Toolbar>
      </AppBar>

      {/* Profile Selector Menu - Styled to match the new design */}
      <Menu
        anchorEl={profilesAnchorEl}
        open={Boolean(profilesAnchorEl)}
        onClose={handleProfilesMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 2,
            p: 1,
            minWidth: 250
                          }
                        }}
      >
        {profiles.length > 0 ? profiles.map((profile) => (
          <MenuItem
            key={profile.id}
            onClick={() => handleProfileSelectAndClose(profile.id)}
            selected={profile.id === currentProfileId}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              px: 2,
              py: 1
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: theme.palette.custom.iconSecondary }}>
              <Avatar
                      sx={{
                  width: 28,
                  height: 28,
                  mr: 1.5,
                  // Use a type-safe function call instead of nested functions
                  bgcolor: (theme: Theme) => getAvatarBackgroundColor(profile.id === currentProfileId, theme)
                }}
              >
                {profile.name.charAt(0).toUpperCase()}
              </Avatar>
                      </ListItemIcon>
                      <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', flexWrap: 'nowrap', overflow: 'hidden' }}>
                  <Typography fontWeight={profile.id === currentProfileId ? 600 : 400} noWrap>
                    {profile.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}
                  >
                    • {profile.investment_profile_template.split('-').map(word =>
                      word.charAt(0).toUpperCase() + word.slice(1)
                    ).join(' ')}
                  </Typography>
                </Box>
              }
              primaryTypographyProps={{ fontWeight: profile.id === currentProfileId ? 600 : 400 }}
                      />
          </MenuItem>
        )) : (
          <MenuItem disabled>No profiles found</MenuItem>
        )}
        <Divider sx={{ my: 1 }} />
        <MenuItem
                onClick={() => {
            handleNavigateAndClose('/profile', { defaultTab: 'investment' });
            handleProfilesMenuClose();
                }}
          disabled={!isPremium()}
                sx={{
            borderRadius: 1,
            color: 'primary.main'
          }}
        >
          <ListItemIcon>
            <AddIcon fontSize="small" sx={{color: !isPremium() ? theme.palette.custom.iconDisabled : theme.palette.custom.iconPrimary}} />
          </ListItemIcon>
          <ListItemText
            primary="Create New Profile"
            sx={{ color: !isPremium() ? 'text.disabled' : 'primary.main' }}
          />
        </MenuItem>
      </Menu>

      {/* Login Menu - Style to match new design */}
      <Menu
        anchorEl={loginMenuAnchorEl}
        open={isLoginMenuOpen}
        onClose={handleLoginMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <MenuItem onClick={() => handleLoginWithProvider('google')} sx={{ borderRadius: 1, mb: 0.5, px: 2, py: 1 }}>
          <ListItemIcon>
            <GoogleIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#4285F4' : theme.palette.info.main }} />
          </ListItemIcon>
          <ListItemText primary="Continue with Google" />
        </MenuItem>
        {/* <MenuItem onClick={() => handleLoginWithProvider('twitter')} sx={{ borderRadius: 1, px: 2, py: 1 }}>
          <ListItemIcon>
            <XIcon fontSize="small" sx={{
              color: (theme) => theme.palette.mode === 'dark' ?
                theme.palette.text.primary :
                theme.palette.text.primary
            }} />
          </ListItemIcon>
          <ListItemText primary="Continue with X (Twitter)" />
        </MenuItem> */}
      </Menu>

      {/* User Menu - Style to match new design */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1,
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <MenuItem onClick={() => {
          navigateWithParams('/profile');
          handleMenuClose();
        }}
        sx={{
          borderRadius: 1,
          mb: 0.5,
          px: 2,
          py: 1
        }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          logout();
          handleMenuClose();
        }}
        sx={{
          borderRadius: 1,
          color: 'error.main',
          px: 2,
          py: 1
        }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>
            Logout
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Mobile Drawer - show only on small screens */}
      {isSmallScreen && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              height: '100%',
              top: (theme) => (isAuthenticated || currentPath === '/') ? `${BANNER_HEIGHT}px` : 0,
              zIndex: 1250,
              // Update to use theme colors
              boxShadow: (theme) => `2px 0 10px ${safeAlpha(
                theme.palette?.custom?.borderColor, 
                0.1,
                theme.palette.primary.main
              )}`
            },
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {/* Drawer Header with Profile Selection */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
              // Use theme background
              bgcolor: (theme) => theme.palette.custom.cardBackground
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                <SMAIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                SMA Insights
              </Typography>
              <IconButton
                onClick={handleDrawerToggle}
                size="small"
                sx={{
                  bgcolor: 'background.paper',
                  boxShadow: (theme) => `0px 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'background.paper',
                    boxShadow: (theme) => `0px 3px 6px ${alpha(theme.palette.primary.main, 0.2)}`,
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Box>

            {/* Add Search Box in the Drawer */}
            {isAuthenticated && (currentUserRole === 'free' || currentUserRole === 'base' || currentUserRole === 'premium' || currentUserRole === 'premium_plus') && (
              <Paper
                elevation={1}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: 48,
                  borderRadius: '24px',
                  mb: 2,
                  border: 1,
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                  overflow: 'hidden',
                  opacity: isPremium() ? 1 : 0.6,
                }}
              >
                <SearchIconMui sx={{ fontSize: '1.25rem', ml: 2, mr: 1, color: theme.palette.custom.iconSecondary }} />
                <Autocomplete
                  freeSolo
                  fullWidth
                  options={autocompleteOptions}
                  value={searchTicker}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input') {
                      setSearchTicker(newInputValue);
                    } else if (reason === 'clear') {
                        setSearchTicker('');
                    }
                  }}
                  onChange={(event, newValue) => {
                    resolveAndNavigateTickerSearch(newValue);
                  }}
                  disabled={!isPremium()}
                  sx={{
                    flex: 1,
                    '& .MuiOutlinedInput-root': {
                      height: 48,
                      paddingLeft: '0px !important',
                       '& .MuiAutocomplete-input': {
                         padding: '12.5px 4px 12.5px 0px',
                       },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search ticker or company..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { border: 'none' },
                          '&:hover fieldset': { border: 'none' },
                          '&.Mui-focused fieldset': { border: 'none' },
                          height: 48, // Ensure TextField root maintains height
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                    />
                  )}
                />
              </Paper>
            )}

            {/* Profile Selector for Mobile */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                // Use theme-aware values for background and colors
                backgroundColor: (theme) => theme.palette.background.paper,
                borderRadius: '28px',
                border: 1,
                borderColor: (theme) => theme.palette.divider,
                px: 2,
                py: 1.5,
                height: 48,
                width: '100%',
                // Use updated shadow with fallback
                boxShadow: (theme) => `0px 2px 4px ${safeAlpha(
                  theme.palette?.custom?.borderColor, 
                  0.2,
                  theme.palette.divider
                )}`,
                cursor: isPremium() ? 'pointer' : 'not-allowed',
                opacity: isPremium() ? 1 : 0.6,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: (theme) => isPremium() ?
                    `0px 3px 6px ${safeAlpha(
                      theme.palette?.custom?.borderColor, 
                      0.2,
                      theme.palette.primary.main
                    )}` : 'none'
                }
              }}
              onClick={() => {
                if (isPremium()) {
                  setMobileProfilesOpen(!mobileProfilesOpen);
                }
              }}
            >
              <Box sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                overflow: 'hidden'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: 'calc(100% - 24px)', overflow: 'hidden' }}>
                  <Typography fontWeight="medium" variant="subtitle1" noWrap>
                    {currentProfile?.name || "Select Profile"}
                  </Typography>
                  {currentProfile?.investment_profile_template && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }} // Keep hidden on xs
                    >
                      • {currentProfile.investment_profile_template.split('-').map(word =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </Typography>
                  )}
                </Box>
                {isPremium() && (
                  mobileProfilesOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />
                )}
              </Box>
            </Box>
          </Box>

          {/* Profile Selection Sub-menu */}
          {mobileProfilesOpen && isPremium() && (
            <Box sx={{
              p: 2,
              bgcolor: (theme) => theme.palette?.custom?.cardBackground ?? theme.palette.background.paper,
              borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
            }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 500 }}>
                SELECT PROFILE
              </Typography>
              <List disablePadding>
                {profiles.length > 0 ? profiles.map((profile) => (
                  <ListItem key={profile.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => handleProfileSelectAndClose(profile.id)}
                      selected={profile.id === currentProfileId}
                      sx={{
                        py: 1,
                        borderRadius: '12px',
                        bgcolor: (theme) => getProfileBackgroundColor(profile.id === currentProfileId, theme),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: (theme) => getProfileHoverBackgroundColor(profile.id === currentProfileId, theme)
                        }
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          mr: 1.5,
                          bgcolor: (theme) => getAvatarBackgroundColor(profile.id === currentProfileId, theme)
                        }}
                      >
                        {profile.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%', flexWrap: 'nowrap', overflow: 'hidden' }}>
                            <Typography
                              fontWeight={profile.id === currentProfileId ? 600 : 400}
                              noWrap
                              // Color needs adjustment for selected state in dark mode
                              sx={{
                                color: profile.id === currentProfileId ?
                                  (theme: Theme) => theme.palette.mode === 'dark' ? 'text.primary' : theme.palette.primary.contrastText
                                  : 'text.primary'
                              }}
                            >
                              {profile.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}
                            >
                              • {profile.investment_profile_template.split('-').map(word =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                              ).join(' ')}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ fontWeight: profile.id === currentProfileId ? 600 :  400 }}
                      />
                    </ListItemButton>
                  </ListItem>
                )) : (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', p: 1 }}>
                    No profiles found
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => {
                      navigateWithParams('/profile', { defaultTab: 'investment' });
                      handleDrawerToggle();
                      setMobileProfilesOpen(false);
                    }}
                    sx={{
                      py: 1,
                      borderRadius: '12px',
                      bgcolor: 'background.default',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
                      <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Create New Profile"
                      primaryTypographyProps={{ color: 'primary.main', fontWeight: 500 }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          )}

          {/* Main Navigation List */}
          <List sx={{ p: 1 }}>
            {/* Navigation Links for Unregistered Users */}
            {!isAuthenticated && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
                  Navigation
                </Typography>

                {/* Home */}
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      navigateWithParams('/');
                      handleDrawerToggle();
                    }}
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      bgcolor: currentPath === '/' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          alpha(theme.palette.info.main, 0.16) :
                          alpha(theme.palette.primary.main, 0.12)
                        : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{
                      color: currentPath === '/' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          theme.palette.info.main :
                          theme.palette.primary.main
                        : 'inherit'
                    }}>
                      <HomeIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Home"
                      primaryTypographyProps={{
                        fontWeight: currentPath === '/' ? 600 : 400,
                        sx: {
                          color: currentPath === '/' ?
                            (theme: Theme) => theme.palette.mode === 'dark' ?
                              theme.palette.info.main :
                              theme.palette.primary.main
                            : 'inherit'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>



                {/* About Us */}
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      navigateWithParams('/about');
                      handleDrawerToggle();
                    }}
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      bgcolor: currentPath === '/about' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          alpha(theme.palette.info.main, 0.16) :
                          alpha(theme.palette.primary.main, 0.12)

                        : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{
                      color: currentPath === '/about' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          theme.palette.info.main :
                          theme.palette.primary.main
                        : 'inherit'
                    }}>
                      <InfoIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="About Us"
                      primaryTypographyProps={{
                        fontWeight: currentPath === '/about' ? 600 : 400,
                        sx: {
                          color: currentPath === '/about' ?
                            (theme: Theme) => theme.palette.mode === 'dark' ?
                              theme.palette.info.main :
                              theme.palette.primary.main
                            : 'inherit'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                {/* FAQ */}
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      navigateWithParams('/faq');
                      handleDrawerToggle();
                    }}
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      bgcolor: currentPath === '/faq' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          alpha(theme.palette.info.main, 0.16) :
                          alpha(theme.palette.primary.main, 0.12)
                        : 'transparent',
                    }}
                  >
                    <ListItemIcon sx={{
                      color: currentPath === '/faq' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          theme.palette.info.main :
                          theme.palette.primary.main
                        : 'inherit'
                    }}>
                      <HelpIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="FAQ"
                      primaryTypographyProps={{
                        fontWeight: currentPath === '/faq' ? 600 : 400,
                        sx: {
                          color: currentPath === '/faq' ?
                            (theme: Theme) => theme.palette.mode === 'dark' ?
                              theme.palette.info.main :
                              theme.palette.primary.main
                            : 'inherit'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                <Divider sx={{ my: 1 }} />
              </>
            )}

            {/* Simplified Navigation for Mobile */}
            {isAuthenticated && (currentUserRole === 'free' || currentUserRole === 'base' || currentUserRole === 'premium' || currentUserRole === 'premium_plus') && (
              <>
                <Typography variant="overline" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
                  Market Analysis
                </Typography>

                {/* Dashboard */}
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      if (currentProfileId) {
                        handleMarketAnalysisViewChange('dashboard');
                        handleDrawerToggle();
                      }
                    }}
                    disabled={!currentProfileId}
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      bgcolor: shouldHighlight && currentProfileSection === 'analysis' && searchParams.get('view') === 'dashboard' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          alpha(theme.palette.info.main, 0.16) :
                          alpha(theme.palette.primary.main, 0.12)
                        : 'transparent',
                      opacity: !currentProfileId ? 0.6 : 1,
                    }}
                  >
                    <ListItemIcon sx={{
                      color: shouldHighlight && currentProfileSection === 'analysis' && searchParams.get('view') === 'dashboard' ?
                        (theme: Theme) => theme.palette.mode === 'dark' ?
                          theme.palette.info.main :
                          theme.palette.primary.contrastText // Use contrast text for light mode selected
                        : 'inherit'
                    }}>
                      <DashboardIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Dashboard"
                      primaryTypographyProps={{
                        fontWeight: shouldHighlight && currentProfileSection === 'analysis' && searchParams.get('view') === 'dashboard' ? 600 : 400,
                        sx: {
                          color: shouldHighlight && currentProfileSection === 'analysis' && searchParams.get('view') === 'dashboard' ?
                            (theme: Theme) => theme.palette.mode === 'dark' ?
                              theme.palette.info.main :
                              theme.palette.primary.contrastText
                            : 'inherit'
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                {/* Reports */}
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      setMobileReportsOpen(!mobileReportsOpen);
                    }}
                    disabled={!(isPremium() || isBase()) || !currentProfileId}
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      bgcolor: shouldHighlight && currentProfileSection === 'analysis' && ['questions', 'recommendations', 'insights'].includes(searchParams.get('view') ?? '') ? 'primary.main' : 'transparent',
                      opacity: (!(isPremium() || isBase()) || !currentProfileId) ? 0.6 : 1,
                    }}
                  >
                    <ListItemIcon sx={{
                      color: shouldHighlight && currentProfileSection === 'analysis' && ['questions', 'recommendations', 'insights'].includes(searchParams.get('view') ?? '') ? 'white' : 'inherit'
                    }}>
                      <InsightsIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Reports"
                      primaryTypographyProps={{
                        fontWeight: shouldHighlight && currentProfileSection === 'analysis' && ['questions', 'recommendations', 'insights'].includes(searchParams.get('view') ?? '') ? 600 : 400,
                        color: shouldHighlight && currentProfileSection === 'analysis' && ['questions', 'recommendations', 'insights'].includes(searchParams.get('view') ?? '') ? 'white' : 'inherit'
                      }}
                    />
                    {(isPremium() || isBase()) && currentProfileId && (
                      mobileReportsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Reports submenu items */}
                {mobileReportsOpen && (isPremium() || isBase()) && currentProfileId && (
                  <Box sx={{ pl: 4 }}>
                    <List disablePadding>
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => {
                            handleMarketAnalysisViewChange('questions');
                            handleDrawerToggle();
                          }}
                          sx={{
                            borderRadius: '10px',
                            py: 1,
                            bgcolor: searchParams.get('view') === 'questions' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                alpha(theme.palette.info.main, 0.12) :
                                alpha(theme.palette.primary.light, 0.5) // Adjusted alpha for light mode sub-selection
                              : 'transparent',
                          }}
                        >
                          <ListItemIcon sx={{
                            minWidth: 36,
                            color: searchParams.get('view') === 'questions' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main // Use primary.main for text color in light mode sub-selection
                              : 'inherit'
                          }}>
                            <QuestionAnswerIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Key Questions"
                            primaryTypographyProps={{
                              sx: {
                                color: searchParams.get('view') === 'questions' ?
                                  (theme: Theme) => theme.palette.mode === 'dark' ?
                                    theme.palette.info.main :
                                    theme.palette.primary.main
                                  : 'inherit'
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => {
                            handleMarketAnalysisViewChange('recommendations');
                            handleDrawerToggle();
                          }}
                          sx={{
                            borderRadius: '10px',
                            py: 1,
                            bgcolor: searchParams.get('view') === 'recommendations' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                alpha(theme.palette.info.main, 0.12) :
                                alpha(theme.palette.primary.light, 0.5)
                              : 'transparent',
                          }}
                        >
                          <ListItemIcon sx={{
                            minWidth: 36,
                            color: searchParams.get('view') === 'recommendations' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }}>
                            <RecommendationsIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Recommendations"
                            primaryTypographyProps={{
                              sx: {
                                color: searchParams.get('view') === 'recommendations' ?
                                  (theme: Theme) => theme.palette.mode === 'dark' ?
                                    theme.palette.info.main :
                                    theme.palette.primary.main
                                  : 'inherit'
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => {
                            handleMarketAnalysisViewChange('insights');
                            handleDrawerToggle();
                          }}
                          sx={{
                            borderRadius: '10px',
                            py: 1,
                            bgcolor: searchParams.get('view') === 'insights' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                alpha(theme.palette.info.main, 0.12) :
                                alpha(theme.palette.primary.light, 0.5)
                              : 'transparent',
                          }}
                        >
                          <ListItemIcon sx={{
                            minWidth: 36,
                            color: searchParams.get('view') === 'insights' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }}>
                            <AssignmentTurnedIn fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Complete Report"
                            primaryTypographyProps={{
                              sx: {
                                color: searchParams.get('view') === 'insights' ?
                                  (theme: Theme) => theme.palette.mode === 'dark' ?
                                    theme.palette.info.main :
                                    theme.palette.primary.main
                                  : 'inherit'
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Box>
                )}

                {/* Analysis Submenu */}
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => {
                      setMobileAnalysisOpen(!mobileAnalysisOpen);
                    }}
                    disabled={!(isPremium() || isBase()) || !currentProfileId}
                    sx={{
                      borderRadius: '10px',
                      py: 1.5,
                      bgcolor: shouldHighlight && currentProfileSection === 'analysis' && ['sectors', 'market-caps'].includes(searchParams.get('view') ?? '') ? 'primary.main' : 'transparent',
                      opacity: (!(isPremium() || isBase()) || !currentProfileId) ? 0.6 : 1,
                    }}
                  >
                    <ListItemIcon sx={{
                      color: shouldHighlight && currentProfileSection === 'analysis' && ['sectors', 'market-caps'].includes(searchParams.get('view') ?? '') ? 'white' : 'inherit'
                    }}>
                      <SectorIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Analysis"
                      primaryTypographyProps={{
                        fontWeight: shouldHighlight && currentProfileSection === 'analysis' && ['sectors', 'market-caps'].includes(searchParams.get('view') ?? '') ? 600 : 400,
                        color: shouldHighlight && currentProfileSection === 'analysis' && ['sectors', 'market-caps'].includes(searchParams.get('view') ?? '') ? 'white' : 'inherit'
                      }}
                    />
                    {(isPremium() || isBase()) && currentProfileId && (
                      mobileAnalysisOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Analysis submenu items */}
                {mobileAnalysisOpen && (isPremium() || isBase()) && currentProfileId && (
                  <Box sx={{ pl: 4 }}>
                    <List disablePadding>
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => {
                            handleMarketAnalysisViewChange('sectors');
                            handleDrawerToggle();
                          }}
                          sx={{
                            borderRadius: '10px',
                            py: 1,
                            bgcolor: searchParams.get('view') === 'sectors' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                alpha(theme.palette.info.main, 0.12) :
                                alpha(theme.palette.primary.light, 0.5)
                              : 'transparent',
                          }}
                        >
                          <ListItemIcon sx={{
                            minWidth: 36,
                            color: searchParams.get('view') === 'sectors' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }}>
                            <SectorIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Sector Analysis"
                            primaryTypographyProps={{
                              sx: {
                                color: searchParams.get('view') === 'sectors' ?
                                  (theme: Theme) => theme.palette.mode === 'dark' ?
                                    theme.palette.info.main :
                                    theme.palette.primary.main
                                  : 'inherit'
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          onClick={() => {
                            handleMarketAnalysisViewChange('market-caps');
                            handleDrawerToggle();
                          }}
                          sx={{
                            borderRadius: '10px',
                            py: 1,
                            bgcolor: searchParams.get('view') === 'market-caps' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                alpha(theme.palette.info.main, 0.12) :
                                alpha(theme.palette.primary.light, 0.5)
                              : 'transparent',
                          }}
                        >
                          <ListItemIcon sx={{
                            minWidth: 36,
                            color: searchParams.get('view') === 'market-caps' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }}>
                            <MarketCapIcon fontSize="small" /> {/* Use renamed icon */}
                          </ListItemIcon>
                          <ListItemText
                            primary="Market Cap Analysis"
                            primaryTypographyProps={{
                              sx: {
                                color: searchParams.get('view') === 'market-caps' ?
                                  (theme: Theme) => theme.palette.mode === 'dark' ?
                                    theme.palette.info.main :
                                    theme.palette.primary.main
                                  : 'inherit'
                              }
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Box>
                )}

                <Divider sx={{ 
                  my: 1,
                  borderColor: (theme) => theme.palette?.custom?.borderColor || theme.palette.divider
                }} />

              </>
            )}

            {/* Tools */}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  setMobileToolsOpen(!mobileToolsOpen);
                }}
                disabled={!isAuthenticated}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  bgcolor: currentPath === '/tools' ? 'primary.main' : 'transparent',
                  opacity: !isAuthenticated ? 0.6 : 1,
                }}
              >
                <ListItemIcon sx={{
                  color: currentPath === '/tools' ? 'white' : 'inherit'
                }}>
                  <ToolsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Tools"
                  primaryTypographyProps={{
                    fontWeight: currentPath === '/tools' ? 600 : 400,
                    color: currentPath === '/tools' ? 'white' : 'inherit'
                  }}
                />
                {isAuthenticated && (
                  mobileToolsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />
                )}
              </ListItemButton>
            </ListItem>

            {/* Tools submenu items */}
            {mobileToolsOpen && isAuthenticated && (
              <Box sx={{ pl: 4 }}>
                <List disablePadding>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        navigateWithParams('/tools', { section: 'market-etfs' });
                        handleDrawerToggle();
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        bgcolor: currentPath === '/tools' && searchParams.get('section') === 'market-etfs' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: currentPath === '/tools' && searchParams.get('section') === 'market-etfs' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            theme.palette.info.main :
                            theme.palette.primary.main
                          : 'inherit'
                      }}>
                        <ScatterPlot fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Market ETFs Analysis"
                        primaryTypographyProps={{
                          sx: {
                            color: currentPath === '/tools' && searchParams.get('section') === 'market-etfs' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        navigateWithParams('/tools', { section: 'sector-etfs' });
                        handleDrawerToggle();
                      }}
                      disabled={isFree()}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        bgcolor: currentPath === '/tools' && searchParams.get('section') === 'sector-etfs' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                        opacity: isFree() ? 0.6 : 1,
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: currentPath === '/tools' && searchParams.get('section' ) === 'sector-etfs' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            theme.palette.info.main :
                            theme.palette.primary.main
                          : 'inherit'
                      }}>
                        <SectorIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Sector ETFs Analysis"
                        primaryTypographyProps={{
                          sx: {
                            color: currentPath === '/tools' && searchParams.get('section') === 'sector-etfs' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        navigateWithParams('/tools', { section: 'discovery' });
                        handleDrawerToggle();
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        bgcolor: currentPath === '/tools' && searchParams.get('section') === 'discovery' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                      }}
                      disabled={!canAccessDiscovery()}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: currentPath === '/tools' && searchParams.get('section') === 'discovery' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            theme.palette.info.main :
                            theme.palette.primary.main
                          : 'inherit'
                      }}>
                        <StockDiscoveryIcon sx={{color: theme.palette.custom.iconPrimary}} fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Stock Discovery"
                        primaryTypographyProps={{
                          sx: {
                            color: currentPath === '/tools' && searchParams.get('section') === 'discovery' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Box>
            )}

            {/* Market Factors */}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  setMobileMarketFactorsOpen(!mobileMarketFactorsOpen);
                }}
                disabled={!(isPremium() || isBase()) || !currentProfileId}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  bgcolor: shouldHighlight && currentProfileSection === 'analysis' && ['performance', 'risk-level', 'money-flow'].includes(searchParams.get('view') ?? '') ? 'primary.main' : 'transparent',
                  opacity: (!(isPremium() || isBase()) || !currentProfileId) ? 0.6 : 1,
                }}
              >
                <ListItemIcon sx={{
                  color: shouldHighlight && currentProfileSection === 'analysis' && ['performance', 'risk-level', 'money-flow'].includes(searchParams.get('view') ?? '') ? 'white' : 'inherit'
                }}>
                  <Tune />
                </ListItemIcon>
                <ListItemText
                  primary="Market Factors"
                  primaryTypographyProps={{
                    fontWeight: shouldHighlight && currentProfileSection === 'analysis' && ['performance', 'risk-level', 'money-flow'].includes(searchParams.get('view') ?? '') ? 600 : 400,
                    color: shouldHighlight && currentProfileSection === 'analysis' && ['performance', 'risk-level', 'money-flow'].includes(searchParams.get('view') ?? '') ? 'white' : 'inherit'
                  }}
                />
                {(isPremium() || isBase()) && currentProfileId && (
                  mobileMarketFactorsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />
                )}
              </ListItemButton>
            </ListItem>

            {/* Market Factors submenu items */}
            {mobileMarketFactorsOpen && (isPremium() || isBase()) && currentProfileId && (
              <Box sx={{ pl: 4 }}>
                <List disablePadding>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        handleMarketAnalysisViewChange('performance');
                        handleDrawerToggle();
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        // Use a direct reference to the theme in a single function
                        bgcolor: (theme: Theme) => 
                          searchParams.get('view') === 'performance' 
                            ? theme.palette.mode === 'dark'
                              ? alpha(theme.palette.info.main, 0.12)
                              : alpha(theme.palette.primary.light, 0.5)
                            : 'transparent'
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: searchParams.get('view') === 'performance' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            theme.palette.info.main :
                            theme.palette.primary.main
                          : 'inherit'
                      }}>
                        <TrendingUpIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Performance"
                        primaryTypographyProps={{
                          sx: {
                            color: searchParams.get('view') === 'performance' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        handleMarketAnalysisViewChange('risk-level');
                        handleDrawerToggle();
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        bgcolor: searchParams.get('view') === 'risk-level' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: searchParams.get('view') === 'risk-level' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            theme.palette.info.main :
                            theme.palette.primary.main
                          : 'inherit'
                      }}>
                        <WarningIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Risk Level"
                        primaryTypographyProps={{
                          sx: {
                            color: searchParams.get('view') === 'risk-level' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      onClick={() => {
                        handleMarketAnalysisViewChange('money-flow');
                        handleDrawerToggle();
                      }}
                      sx={{
                        borderRadius: '10px',
                        py: 1,
                        bgcolor: searchParams.get('view') === 'money-flow' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            alpha(theme.palette.info.main, 0.12) :
                            alpha(theme.palette.primary.light, 0.5)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 36,
                        color: searchParams.get('view') === 'money-flow' ?
                          (theme: Theme) => theme.palette.mode === 'dark' ?
                            theme.palette.info.main :
                            theme.palette.primary.main
                          : 'inherit'
                      }}>
                        <MonetizationOnIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Money Flow"
                        primaryTypographyProps={{
                          sx: {
                            color: searchParams.get('view') === 'money-flow' ?
                              (theme: Theme) => theme.palette.mode === 'dark' ?
                                theme.palette.info.main :
                                theme.palette.primary.main
                              : 'inherit'
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </List>
              </Box>
            )}

            <Divider sx={{ 
              my: 1,
              borderColor: (theme) => theme.palette?.custom?.borderColor || theme.palette.divider
            }} />

            {/* Watchlists */}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  if (isPremium() && currentProfileId) {
                    handleSubTabChange('watchlists');
                    handleDrawerToggle();
                  }
                }}
                disabled={!isPremium() || !currentProfileId}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  bgcolor: currentProfileSection === 'watchlists' ? 'primary.main' : 'transparent',
                  opacity: !isPremium() || !currentProfileId ? 0.6 : 1,
                }}
              >
                <ListItemIcon sx={{ color: currentProfileSection === 'watchlists' ? 'white' : 'inherit' }}>
                  <WatchlistIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Watchlists"
                  primaryTypographyProps={{
                    fontWeight: currentProfileSection === 'watchlists' ? 600 : 400,
                    color: currentProfileSection === 'watchlists' ? 'white' : 'inherit'
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Screeners */}
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  if (isPremium() && currentProfileId) {
                    handleSubTabChange('screeners');
                    handleDrawerToggle();
                  }
                }}
                disabled={!isPremium() || !currentProfileId}
                sx={{
                  borderRadius: '10px',
                  py: 1.5,
                  bgcolor: currentProfileSection === 'screeners' ? 'primary.main' : 'transparent',
                  opacity: !isPremium() || !currentProfileId ? 0.6 : 1,
                }}
              >
                <ListItemIcon sx={{ color: currentProfileSection === 'screeners' ? 'white' : 'inherit' }}>
                  <ScreenerIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Screeners"
                  primaryTypographyProps={{
                    fontWeight: currentProfileSection === 'screeners' ? 600 : 400,
                    color: currentProfileSection === 'screeners' ? 'white' : 'inherit'
                  }}
                />
              </ListItemButton>
            </ListItem>

            <Divider sx={{ 
              my: 1,
              borderColor: (theme) => theme.palette?.custom?.borderColor || theme.palette.divider
            }} />
          </List> {/* Ensure closing tag exists */}
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: '100%',
          // Removed margin top since header is now static
          overflowY: 'auto',
        }}
      >
        {authLoading || !initialLoadComplete || postLoginPreRefresh ? (
           // Simple initial loading indicator
           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
             <CircularProgress color="primary" thickness={4} size={60} />
             <Typography variant="body2" sx={{ mt: 2 }}>
               {authLoading ? 'Authenticating...' : 'Initializing...'}
             </Typography>
           </Box>
        ) : (
          <Routes>
            {/* Root Route - Conditionally render UnregisteredContent */}
            <Route path="/" element={
              authLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1">Loading...</Typography>
                </Box>
              ) : !isAuthenticated || (isUnregistered()) ? (
                showRegistration ? '' : (
                  <>
                  <UnregisteredContent
                    urlPrimaryBase={urlPrimaryBase}
                    setShowRegistration={setShowRegistration}
                    getBusinessDates={getBusinessDates} // Pass helper function
                  />
                  </>
                )
              ) : (
                <Navigate to="/premium" replace />
              )
            } />

            {/* --- Premium Route passes down the fetched data --- */}
            <Route path="/premium" element={
              hasRole(['free', 'base', 'premium', 'premium_plus']) ? (
                (() => {
                  // Check URL parameters directly here
                  const params = new URLSearchParams(location.search);
                  const view = params.get('view');
                  const ticker = params.get('ticker');

                  if (view === 'ticker' && ticker) {
                    console.log(`Routing to TickerDetailView for ${ticker}`);
                    // Render TickerDetailView when view=ticker&ticker=XYZ is in URL
                    return (
                      <TickerDetailView
                        ticker={ticker}
                        // // Pass profile template for potential API calls within TickerDetailView
                        // profileTemplate={currentProfile?.investment_profile_template}
                        onBack={handleTickerDetailBack} // Pass the back handler
                        // Add other necessary props like api instance if needed
                      />
                    );
                  } else {
                    // Pass the marketReportFull fetched in AppContent
                    return (
                      <PremiumContent
                        urlPrimaryBase={urlPrimaryBase}
                        // No need to pass currentProfileSection/currentProfile, it reads from context/URL
                        // getAuthHeaders={null} // Pass getAuthHeaders if needed by children like Watchlists/Screeners
                        marketReportFull={marketReportFull} // Pass fetched data
                        marketReportLoading={marketReportFull ? false : true} // Pass loading state from context
                        marketReportError={bannerError} // Pass error state
                      />
                    );
                  }
                })()
              ) : (
                <Navigate to="/" replace /> // Redirect if user doesn't have access
              )
            } />
            <Route path="/about" element={<AboutUsPage />} />
            <Route path="/faq" element={<FAQPage />} /> {/* Assuming FAQ content is on About page */}
            <Route path="/contact" element={<ContactPage />} /> {/* Add route for ContactPage */}
            <Route path="/terms" element={<TermsPage />} /> {/* Updated to use dedicated TermsPage component */}
            <Route path="/profile" element={
              isAuthenticated ? ( // Protect profile page
                <UserProfilePage
                  user={isUser(user) ? { // Type guard
                    userName: user?.userName,
                    email: user?.email,
                    role: currentUserRole,
                    provider: user?.provider
                  } : undefined}
                  onLogout={logout}
                  urlPrimaryBase={urlPrimaryBase || ''}
                  onProfilesChange={refreshProfiles}
                  onPlanChange={handlePlanChange}
                />
              ) : (
                <Navigate to="/" replace /> // Redirect if not logged in
              )
            } />
            <Route path="/tools" element={
               isAuthenticated ? ( // Protect tools page
                <InvestorToolsPage
                  urlPrimaryBase={urlPrimaryBase}
                  // Add onTickerSelect using navigateWithParams if tools can link to ticker view
                   onTickerSelect={(ticker) => navigateWithParams('/premium', { view: 'ticker', ticker: ticker })}
                />
               ) : (
                 <Navigate to="/" replace /> // Redirect if not logged in
               )
            } />
                      </Routes>
        )}
      </Box>

      {/* Scroll to top button */}
      {showScrollToTop && (
        <Fade in={showScrollToTop}>
          <IconButton
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.custom.navBg,
              //color: theme.palette.primary.contrastText,
              // boxShadow: (theme) => `0px 2px 8px ${alpha(
              //   theme.palette?.custom?.borderColor || theme.palette.common.black, 
              //   0.2
              // )}`,
              cursor: 'pointer',
              zIndex: 99,
              opacity: 0.9,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: theme.palette.custom.navHoverBg,
              }
              // '&:hover': {
              //   transform: 'scale(1.1)',
              //   boxShadow: (theme) => `0px 4px 12px ${alpha(
              //     theme.palette?.custom?.borderColor || theme.palette.common.black, 
              //     0.3
              //   )}`,
              //  opacity: 1
              //}
            }}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
        </Fade>
      )}

      {/* Footer - ensure it sticks to bottom */}
      <Box
        component="footer"
        sx={{
          mt: 'auto', // Push footer to bottom
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          // Use theme colors for footer
          bgcolor: (theme) => theme.palette.mode === 'dark'
            ? theme.palette?.custom?.cardBackground ?? theme.palette.background.paper
            : theme.palette.primary.main,
          color: (theme) => theme.palette.mode === 'dark' ?
            theme.palette.text.primary :
            theme.palette.primary.contrastText,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="body2" color="inherit">
                Seattle Market Analytics &copy; {new Date().getFullYear()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: (theme) => theme.palette.mode === 'dark' ?
                    'text.secondary' :
                    'rgba(255, 255, 255, 0.7)'
                }}
              >
                {/* Empowering Traders Everywhere. Version: {version} */} {/* Version moved to NewApp.tsx */}
                Empowering Traders Everywhere. Version: {version}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 2 }, mt: { xs: 2, sm: 0 }, justifyContent: 'center' }}>
              <Button
                size="small"
                onClick={() => navigateWithParams('/')}
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: { xs: responsiveFontSizes.mobile.caption, md: responsiveFontSizes.desktop.caption },
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.1) :
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.2),
                  }
                }}
              >
                Home
              </Button>
              <Button
                size="small"
                onClick={() => navigateWithParams('/about')}
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: { xs: responsiveFontSizes.mobile.caption, md: responsiveFontSizes.desktop.caption },
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.1) :
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.2),
                  }
                }}
              >
                About Us
              </Button>
              <Button
                size="small"
                onClick={() => navigateWithParams('/terms')}
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: { xs: responsiveFontSizes.mobile.caption, md: responsiveFontSizes.desktop.caption },
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.1) :
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.2),
                  }
                }}
              >
                Terms
              </Button>
              <Button
                size="small"
                onClick={() => navigateWithParams('/contact')}
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: { xs: responsiveFontSizes.mobile.caption, md: responsiveFontSizes.desktop.caption },
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.1) :
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.2),
                  }
                }}
              >
                Contact
              </Button>
              <Button
                size="small"
                onClick={() => navigateWithParams('/faq')}
                sx={{
                  color: theme.palette.secondary.main,
                  fontSize: { xs: responsiveFontSizes.mobile.caption, md: responsiveFontSizes.desktop.caption },
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.1) :
                      alpha(theme.palette?.custom?.hoverRowBg || theme.palette.common.white, 0.2),
                  }
                }}
              >
                FAQ
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Modals */}
      {showRegistration && (
        <RegistrationWorkflow
          onClose={() => {setShowRegistration(false)}}
          onLogin={() => {}} // Consider triggering login flow after registration
          onComplete={() => setShowRegistration(false)}
          urlBase={urlPrimaryBase}
        />
      )}
      {showSubscriptionManager && isAuthenticated && ( // Only show if logged in
        <SubscriptionManager
          onClose={() => setShowSubscriptionManager(false)}
          onPlanChange={handlePlanChange}
        />
      )}

      {/* Loading Indicator - ensure it's only rendered once */}
      <LoadingIndicator statuses={statuses} singleton={true} />
    </Box>
  );
};

export default AppContent;
